import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { HeroImage } from '@guryou/html-components';
import { randomProvidersActionByKeywordOrAddress, stateIsLoaded } from '@guryou/redux';
import { SkeletonLoader, ProviderCard, Pagination } from '../../components/shared';
import BookSearchForm from './BookSearchForm';
import { useWidgetAuthLockedProvider } from '../../hooks/useWidgetAuthLockedProvider';

const BookSection = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const lockProviderId = useWidgetAuthLockedProvider();
  const randomProviders = useSelector(state => state.randomProviders);
  const [currentCenterPage, setCurrentCenterPage] = useState(1);
  const [currentProfessionalPage, setCurrentProfessionalPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [filterType, setFilterType] = useState('C'); // 'C' for centers, 'P' for professionals
  const [keyword, setKeyword] = useState('');
  const [address, setAddress] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [debouncedAddress, setDebouncedAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Get all data without limits
  const allCenters = randomProviders?.data?.response?.centers || [];
  const allProfessionals = randomProviders?.data?.response?.professionals || [];

  // Filter data based on current filter type
  const currentData = filterType === 'C' ? allCenters : allProfessionals;
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current page items
  const currentPage = filterType === 'C' ? currentCenterPage : currentProfessionalPage;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = currentData.slice(startIndex, endIndex);

  // For display purposes, separate into centers and professionals
  const centers = filterType === 'C' ? paginatedData : [];
  const professionals = filterType === 'P' ? paginatedData : [];

  useEffect(() => {
    if (!lockProviderId) {
      return;
    }
    history.replace(`/it/provider/${lockProviderId}`);
  }, [lockProviderId, history]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (lockProviderId) {
      return undefined;
    }
    const initializeAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places && addressRef.current && !autocompleteRef.current) {
        try {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(addressRef.current, {
            types: ['(cities)'],
            componentRestrictions: { country: 'it' },
          });

          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();

            if (place.formatted_address) {
              setAddress(place.formatted_address);

              // Trigger search immediately when place is selected
              setIsSearching(true);
              dispatch(randomProvidersActionByKeywordOrAddress(debouncedKeyword, place.formatted_address, '', filterType));
              setCurrentCenterPage(1);
              setCurrentProfessionalPage(1);

              setTimeout(() => {
                setIsSearching(false);
              }, 1000);
            }
          });
        } catch (error) {
          console.error('Error initializing Google Places Autocomplete:', error);
        }
      } else {
        setTimeout(initializeAutocomplete, 1000);
      }
    };

    // Wait for Google Maps to load
    if (window.googleMapsLoaded) {
      initializeAutocomplete();
    } else {
      // Check every 500ms if Google Maps is loaded
      const checkInterval = setInterval(() => {
        if (window.googleMapsLoaded) {
          clearInterval(checkInterval);
          initializeAutocomplete();
        }
      }, 500);

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 10000);
    }

    // Cleanup function
    return () => {
      if (autocompleteRef.current && window.google?.maps?.event?.clearInstanceListeners) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [dispatch, debouncedKeyword, filterType, lockProviderId]);

  // Effect to handle initial load completion
  useEffect(() => {
    const hasData = randomProviders?.data?.response;
    if (hasData && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [randomProviders, isInitialLoad]);

  // Debounce effect for keyword
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [keyword]);

  // Debounce effect for address
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAddress(address);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [address]);

  // Unified search effect (avoids duplicate API calls on mount)
  useEffect(() => {
    if (lockProviderId) {
      return undefined;
    }
    setIsSearching(true);
    dispatch(randomProvidersActionByKeywordOrAddress(debouncedKeyword, debouncedAddress, '', filterType));
    // Reset to first page on any filter/search change
    setCurrentCenterPage(1);
    setCurrentProfessionalPage(1);

    // Reset loading state after a short delay
    const timeoutId = setTimeout(() => {
      setIsSearching(false);
    }, 1000);

    // Cleanup timeout
    return () => clearTimeout(timeoutId);
  }, [lockProviderId, filterType, dispatch, debouncedKeyword, debouncedAddress]);

  // Filter functions
  const handleFilterChange = useCallback(type => {
    setFilterType(type);
    // Reset to first page when changing filter
    setCurrentCenterPage(1);
    setCurrentProfessionalPage(1);
  }, []);

  const handleKeywordChange = useCallback(e => {
    setKeyword(e.target.value);
  }, []);

  const handleAddressInputChange = useCallback(e => {
    const value = e.target.value;
    setAddress(value);

    // Address cleared
  }, []);

  const handleSearch = useCallback(() => {
    // Force immediate search without debounce
    setIsSearching(true);
    dispatch(randomProvidersActionByKeywordOrAddress(keyword, address, '', filterType));
    setDebouncedKeyword(keyword);
    setDebouncedAddress(address);
    // Reset to first page when searching
    setCurrentCenterPage(1);
    setCurrentProfessionalPage(1);

    // Reset loading state after a short delay
    setTimeout(() => {
      setIsSearching(false);
    }, 1000);
  }, [dispatch, keyword, address, filterType]);

  const handleClearAddress = useCallback(() => {
    setAddress('');
    setIsSearching(true);
    dispatch(randomProvidersActionByKeywordOrAddress(debouncedKeyword, '', '', filterType));
    setCurrentCenterPage(1);
    setCurrentProfessionalPage(1);
    setTimeout(() => {
      setIsSearching(false);
    }, 1000);
  }, [dispatch, debouncedKeyword, filterType]);

  // Pagination functions for current filter
  const handlePageChange = useCallback(
    page => {
      if (filterType === 'C') {
        setCurrentCenterPage(page);
      } else {
        setCurrentProfessionalPage(page);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [filterType]
  );

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const renderResultsGrid = () => {
    const data = filterType === 'C' ? centers : professionals;
    const cardType = filterType === 'C' ? 'center' : 'professional';

    if (isSearching) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <SkeletonLoader type="compactCard" count={6} />
        </div>
      );
    }

    if (!data?.length) {
      return (
        <div className="text-center py-16">
          <p className="text-gray-600 text-lg">{t('BOOK_SECTION.noResults', 'Nessun risultato trovato')}</p>
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {data.map(card => (
            <ProviderCard key={card.id} provider={card} type={cardType} navigationType="navigation" onViewProfile={() => {}} />
          ))}
        </div>
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={totalItems}
          />
        )}
      </>
    );
  };

  const renderHero = () => (
    <section className="relative flex flex-col items-center justify-center bg-cover bg-no-repeat bg-center min-h-[520px] sm:min-h-[600px] pt-28 md:pt-32 pb-10">
      <div className="absolute inset-0 brightness-50 grayscale bg-cover bg-center -z-10" style={{ backgroundImage: `url(${HeroImage})` }} />
      <div className="py-6 px-4 mx-auto max-w-screen-xl sm:py-10 lg:px-6 w-full">
        <div className="max-w-screen-lg mx-auto text-center mb-8">
          <h1 className="mb-4 text-4xl tracking-tight font-extrabold text-white md:text-5xl">{t('BOOK_SECTION.title')}</h1>
          <p className="mb-8 font-light text-gray-100 sm:text-xl max-w-3xl mx-auto">{t('BOOK_SECTION.description')}</p>
        </div>
        <BookSearchForm
          filterType={filterType}
          onFilterChange={handleFilterChange}
          keyword={keyword}
          onKeywordChange={handleKeywordChange}
          address={address}
          onAddressChange={handleAddressInputChange}
          addressRef={addressRef}
          onClearAddress={handleClearAddress}
          onSearch={handleSearch}
        />
      </div>
    </section>
  );

  if (lockProviderId) {
    return null;
  }

  // Show loading only during initial load
  if (isInitialLoad && (!stateIsLoaded(randomProviders) || !randomProviders?.data?.response)) {
    return (
      <main>
        {renderHero()}
        <section className="bg-white">
          <div className="py-12 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <SkeletonLoader type="compactCard" count={6} />
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      {renderHero()}
      <section className="bg-white">
        <div className="py-12 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6">{renderResultsGrid()}</div>
      </section>
    </main>
  );
};

export default BookSection;
